import React, { useState } from 'react';
import CustomDropdown, { DropdownOption } from 'component/CustomDropdown';
import DateRangeFilter, { DateRangeValue } from 'component/DateRangeFilterDropdown';
import { BackOfficeTrailActivityDetailResp } from 'domain/payload/response/backOfficeTrails/BackOfficeTrailsResp';

interface BackOfficeActivityLogCardProps {
    activities: BackOfficeTrailActivityDetailResp[];
    loading: boolean;
    totalRecords: number;
    totalPages: number;
    currentPage: number;
    pageSize: number;
    onPageChange: (page: number) => void;
    onPageSizeChange: (size: number) => void;
    onDateRangeChange: (value: DateRangeValue) => void;
    activityType: string;
    onActivityTypeChange: (type: string) => void;
    searchTerm: string;
    onSearchChange: (search: string) => void;
    onSearchSubmit: (e: React.FormEvent) => void;
}

const pageSizeOptions: DropdownOption[] = [
    { id: 5, name: '5' },
    { id: 10, name: '10' },
    { id: 25, name: '25' },
    { id: 50, name: '50' },
    { id: 100, name: '100' },
];

const activityTypeOptions: DropdownOption[] = [
    { id: '', name: 'All Activity Types' },
    { id: 'Dashboard Data', name: 'Dashboard Data' },
    { id: 'Project', name: 'Project' },
    { id: 'Manage Field User', name: 'Manage Field User' },
    { id: 'Manage Client', name: 'Manage Client' },
    { id: 'Manage Store', name: 'Manage Store' },
    { id: 'Manage Poi', name: 'Manage Poi' },
    { id: 'Manage Back Office', name: 'Manage Back Office' },
    { id: 'Merchant Partner', name: 'Merchant Partner' },
    { id: 'Form', name: 'Form' },
    { id: 'System Parameter', name: 'System Parameter' },
    { id: 'Tribe', name: 'Tribe' },
    { id: 'Project Config', name: 'Project Config' },
    { id: 'Project Key', name: 'Project Key' },
    { id: 'Project Layout', name: 'Project Layout' },
    { id: 'Project Filter', name: 'Project Filter' },
    { id: 'Project Metric', name: 'Project Metric' },
    { id: 'RBAC', name: 'RBAC' },
    { id: 'Version', name: 'Version' },
    { id: 'PPOB', name: 'PPOB' },
    { id: 'Report Engine', name: 'Report Engine' },
];

const BackOfficeActivityLogCard: React.FC<BackOfficeActivityLogCardProps> = ({
    activities,
    loading,
    totalRecords,
    totalPages,
    currentPage,
    pageSize,
    onPageChange,
    onPageSizeChange,
    onDateRangeChange,
    activityType,
    onActivityTypeChange,
    searchTerm,
    onSearchChange,
    onSearchSubmit,
}) => {
    const [expandedRows, setExpandedRows] = useState<Record<number, boolean>>({});

    const toggleRow = (index: number) => {
        setExpandedRows((prev) => ({
            ...prev,
            [index]: !prev[index],
        }));
    };

    const formatTimestamp = (dateStr?: string): string => {
        if (!dateStr) return '-';
        try {
            const d = new Date(dateStr);
            if (isNaN(d.getTime())) return dateStr;
            const year = d.getFullYear();
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            const hours = String(d.getHours()).padStart(2, '0');
            const minutes = String(d.getMinutes()).padStart(2, '0');
            const seconds = String(d.getSeconds()).padStart(2, '0');
            return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
        } catch {
            return dateStr;
        }
    };

    const formatJson = (payload: any): string => {
        if (payload === null || payload === undefined) return '';
        if (typeof payload === 'string') {
            try {
                const parsed = JSON.parse(payload);
                return JSON.stringify(parsed, null, 2);
            } catch {
                return payload;
            }
        }
        return JSON.stringify(payload, null, 2);
    };

    const hasData = (payload: any): boolean => {
        if (payload === null || payload === undefined || payload === '') return false;
        if (typeof payload === 'object') {
            return Object.keys(payload).length > 0;
        }
        if (typeof payload === 'string') {
            try {
                const parsed = JSON.parse(payload);
                return typeof parsed === 'object' && parsed !== null && Object.keys(parsed).length > 0;
            } catch {
                return payload.trim().length > 0;
            }
        }
        return true;
    };

    return (
        <div className="card border shadow-none">
            <div className="card-header bg-light border-bottom py-3 px-4 d-flex justify-content-between align-items-center">
                <h6 className="mb-0 fw-bold">Activity Log</h6>
                <span
                    className="badge rounded-pill border bg-white text-dark fw-bold px-2.5 py-1"
                    style={{ fontSize: '11px' }}
                >
                    {totalRecords} Records
                </span>
            </div>

            <div className="card-body p-4">
                {/* Filter Toolbar */}
                <div className="row g-2 mb-4 align-items-center justify-content-between">
                    <div className="col-auto">
                        <DateRangeFilter onChange={onDateRangeChange} />
                    </div>

                    <div className="col-auto">
                        <CustomDropdown
                            value={activityType}
                            options={activityTypeOptions}
                            onChange={(opt) => onActivityTypeChange(opt.id ? String(opt.id) : '')}
                            placeholder="All Activity Types"
                            containerStyle={{ width: '180px' }}
                        />
                    </div>

                    <div className="col-12 col-md-5">
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Search log..."
                            value={searchTerm}
                            onChange={(e) => onSearchChange(e.target.value)}
                        />
                    </div>

                    <div className="col-auto">
                        <button type="button" className="btn btn-primary px-3" onClick={onSearchSubmit}>
                            <i className="fa-solid fa-search"></i>
                        </button>
                    </div>
                </div>

                {/* Table */}
                {loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : (
                    <div className="table-responsive">
                        <table className="table align-middle table-hover" style={{ tableLayout: 'fixed', width: '100%' }}>
                            <thead className="table-light">
                                <tr>
                                    <th className="py-2.5 fw-bold small text-dark" style={{ width: '16%' }}>
                                        Timestamp
                                    </th>
                                    <th className="py-2.5 fw-bold small text-dark" style={{ width: '15%' }}>
                                        Activity Type
                                    </th>
                                    <th className="py-2.5 fw-bold small text-dark" style={{ width: '12%' }}>
                                        Action
                                    </th>
                                    <th className="py-2.5 fw-bold small text-dark" style={{ width: '12%' }}>
                                        Detail
                                    </th>
                                    <th className="py-2.5 fw-bold small text-dark" style={{ width: '18%' }}>
                                        User Agent
                                    </th>
                                    <th className="py-2.5 fw-bold small text-dark text-center" style={{ width: '12%' }}>
                                        Payload
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {activities.length > 0 ? (
                                    activities.map((activity, index) => {
                                        const isExpanded = !!expandedRows[index];
                                        const hasBefore = hasData(activity.payloadBefore);
                                        const hasAfter = hasData(activity.payloadAfter);

                                        return (
                                            <React.Fragment key={index}>
                                                <tr>
                                                    <td className="small text-muted text-truncate" title={formatTimestamp(activity.timestamp)}>
                                                        {formatTimestamp(activity.timestamp)}
                                                    </td>
                                                    <td className="fw-bold text-dark small text-truncate" title={activity.activityType || '-'}>
                                                        {activity.activityType || '-'}
                                                    </td>
                                                    <td className="fw-semibold text-dark small text-truncate" title={activity.action || '-'}>
                                                        {activity.action || '-'}
                                                    </td>
                                                    <td className="small text-secondary text-truncate" title={activity.detail || '-'}>
                                                        {activity.detail || '-'}
                                                    </td>
                                                    <td className="small text-dark" title={activity.userAgentInfo || '-'}>
                                                        {activity.userAgentInfo || '-'}
                                                    </td>
                                                    <td className="text-center">
                                                        {isExpanded ? (
                                                            <button
                                                                type="button"
                                                                className="btn btn-primary rounded-pill btn-sm px-3 py-1 align-items-center shadow-none"
                                                                style={{ fontSize: '12px', fontWeight: 500 }}
                                                                onClick={() => toggleRow(index)}
                                                            >
                                                                <i className="fa-solid fa-chevron-up me-1"></i> Hide
                                                            </button>
                                                        ) : (
                                                            <button
                                                                type="button"
                                                                className="btn btn-outline-primary rounded-pill btn-sm px-3 py-1 align-items-center shadow-none"
                                                                style={{ fontSize: '12px', fontWeight: 500 }}
                                                                onClick={() => toggleRow(index)}
                                                            >
                                                                <i className="fa-regular fa-eye me-1"></i> View
                                                            </button>
                                                        )}
                                                    </td>
                                                </tr>

                                                {/* Expanded Payload Detail Row */}
                                                {isExpanded && (
                                                    <tr className="table-light">
                                                        <td colSpan={6} className="p-3">
                                                            <div className="card border rounded-3 bg-white shadow-sm">
                                                                <div className="card-header bg-white border-bottom py-2.5 px-3 d-flex justify-content-between align-items-center">
                                                                    <div className="d-flex align-items-center">
                                                                        <i className="fa-solid fa-code text-primary me-2"></i>
                                                                        <span className="fw-bold small text-dark">
                                                                            Payload Detail — {activity.activityType} ({activity.action})
                                                                        </span>
                                                                    </div>
                                                                    <button
                                                                        type="button"
                                                                        className="btn-close"
                                                                        style={{ fontSize: '10px' }}
                                                                        onClick={() => toggleRow(index)}
                                                                        aria-label="Close"
                                                                    ></button>
                                                                </div>

                                                                <div className="card-body p-3">
                                                                    <div className="row g-3">
                                                                        {/* Payload Before */}
                                                                        <div className="col-12 col-md-6" style={{ minWidth: 0 }}>
                                                                            <div className="d-flex align-items-center mb-2">
                                                                                <i className="fa-solid fa-clock-rotate-left text-muted me-2 small"></i>
                                                                                <span className="fw-semibold small text-muted">
                                                                                    Payload Before
                                                                                </span>
                                                                            </div>
                                                                            {hasBefore ? (
                                                                                <div
                                                                                    className="p-3 rounded text-white"
                                                                                    style={{
                                                                                        backgroundColor: '#1e293b',
                                                                                        minHeight: '180px',
                                                                                        maxHeight: '350px',
                                                                                        overflow: 'auto',
                                                                                    }}
                                                                                >
                                                                                    <pre
                                                                                        className="mb-0 text-white"
                                                                                        style={{
                                                                                            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                                                                                            fontSize: '12px',
                                                                                            lineHeight: '1.5',
                                                                                            whiteSpace: 'pre-wrap',
                                                                                            wordBreak: 'break-all',
                                                                                        }}
                                                                                    >
                                                                                        {formatJson(activity.payloadBefore)}
                                                                                    </pre>
                                                                                </div>
                                                                            ) : (
                                                                                <div
                                                                                    className="p-4 text-center text-muted bg-light rounded border d-flex align-items-center justify-content-center"
                                                                                    style={{ minHeight: '180px' }}
                                                                                >
                                                                                    <div className="d-flex align-items-center justify-content-center">
                                                                                        <i className="fa-regular fa-circle-xmark me-2 text-secondary"></i>
                                                                                        <span className="small">
                                                                                            No data before ({activity.action || 'Create'})
                                                                                        </span>
                                                                                    </div>
                                                                                </div>
                                                                            )}
                                                                        </div>

                                                                        {/* Payload After */}
                                                                        <div className="col-12 col-md-6" style={{ minWidth: 0 }}>
                                                                            <div className="d-flex align-items-center mb-2">
                                                                                <i className="fa-solid fa-circle-check text-success me-2 small"></i>
                                                                                <span className="fw-semibold small text-muted">
                                                                                    Payload After
                                                                                </span>
                                                                            </div>
                                                                            {hasAfter ? (
                                                                                <div
                                                                                    className="p-3 rounded text-white"
                                                                                    style={{
                                                                                        backgroundColor: '#1e293b',
                                                                                        minHeight: '180px',
                                                                                        maxHeight: '350px',
                                                                                        overflow: 'auto',
                                                                                    }}
                                                                                >
                                                                                    <pre
                                                                                        className="mb-0 text-white"
                                                                                        style={{
                                                                                            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                                                                                            fontSize: '12px',
                                                                                            lineHeight: '1.5',
                                                                                            whiteSpace: 'pre-wrap',
                                                                                            wordBreak: 'break-all',
                                                                                        }}
                                                                                    >
                                                                                        {formatJson(activity.payloadAfter)}
                                                                                    </pre>
                                                                                </div>
                                                                            ) : (
                                                                                <div
                                                                                    className="p-4 text-center text-muted bg-light rounded border d-flex align-items-center justify-content-center"
                                                                                    style={{ minHeight: '180px' }}
                                                                                >
                                                                                    <div className="d-flex align-items-center justify-content-center">
                                                                                        <i className="fa-regular fa-circle-xmark me-2 text-secondary"></i>
                                                                                        <span className="small">No data after</span>
                                                                                    </div>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan={6} className="text-center py-4 text-muted small">
                                            No activity yet
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Pagination */}
                <div className="d-flex justify-content-end align-items-center mt-3 pt-2">
                    <div className="d-flex align-items-center">
                        <div className="me-4 d-flex align-items-center">
                            <label className="me-2 small text-muted">Rows:</label>
                            <CustomDropdown
                                value={pageSize}
                                options={pageSizeOptions}
                                onChange={(opt) => {
                                    onPageSizeChange(Number(opt.id));
                                }}
                                align="top"
                            />
                        </div>

                        <span className="me-3 small text-muted">
                            {totalRecords > 0
                                ? `${(currentPage - 1) * pageSize + 1}–${Math.min(currentPage * pageSize, totalRecords)} of ${totalRecords}`
                                : '0 of 0'}
                        </span>

                        <nav>
                            <ul className="pagination pagination-sm mb-0">
                                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                    <button
                                        className="page-link"
                                        onClick={() => onPageChange(1)}
                                        style={{ border: 0 }}
                                        disabled={currentPage === 1}
                                    >
                                        <i className="fa fa-angle-double-left"></i>
                                    </button>
                                </li>
                                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                    <button
                                        className="page-link"
                                        onClick={() => onPageChange(currentPage - 1)}
                                        style={{ border: 0 }}
                                        disabled={currentPage === 1}
                                    >
                                        <i className="fa fa-angle-left"></i>
                                    </button>
                                </li>
                                <li className="page-item disabled">
                                    <span className="page-link fw-bold text-dark" style={{ border: 0 }}>
                                        {currentPage}
                                    </span>
                                </li>
                                <li className={`page-item ${currentPage >= totalPages ? 'disabled' : ''}`}>
                                    <button
                                        className="page-link"
                                        onClick={() => onPageChange(currentPage + 1)}
                                        style={{ border: 0 }}
                                        disabled={currentPage >= totalPages}
                                    >
                                        <i className="fa fa-angle-right"></i>
                                    </button>
                                </li>
                                <li className={`page-item ${currentPage >= totalPages ? 'disabled' : ''}`}>
                                    <button
                                        className="page-link"
                                        onClick={() => onPageChange(totalPages)}
                                        style={{ border: 0 }}
                                        disabled={currentPage >= totalPages}
                                    >
                                        <i className="fa fa-angle-double-right"></i>
                                    </button>
                                </li>
                            </ul>
                        </nav>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BackOfficeActivityLogCard;
